/**
 * @file index.tsx
 *
 * @fileoverview Renders bottom navigation sub menu.
 */

import React from "react";
import styles from "./bottomSubMenu.module.scss";
import { motion, MotionProps } from "framer-motion";
import { ReactNode, useMemo } from "react";
import { createPortal } from "react-dom";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import { withExitAnimation } from "../../../../hocs/withExitAnimation";
// Define a new interface that extends MotionProps
type MotionButtonProps = MotionProps & React.HTMLAttributes<HTMLButtonElement>;
const CustomButtonMotion = motion<MotionButtonProps>("button");

type MotionDivProps = MotionProps & React.HTMLAttributes<HTMLDivElement>;
const CustomDivMotion = motion<MotionDivProps>("div");

interface BottomSubMenuProps {
  /**
   * The children.
   */
  Items: ReactNode[];
  /**
   * The collapsed flag.
   */
  collapsed: boolean;
  /**
   * On overlay click callback.
   */
  onOverlayClick?(): void;
}

export function BottomSubMenu({
  Items,
  collapsed,
  onOverlayClick,
}: BottomSubMenuProps) {
  const bodyElement = useMemo(() => document.querySelector("body"), []);

  return (
    <>
      {withExitAnimation(
        <CustomDivMotion
          initial={{
            height: 0,
          }}
          animate={{
            height: "auto",
          }}
          exit={{
            height: 0,
          }}
          transition={{
            duration: 0.3,
          }}
          className={styles.bottomSubMenu}
        >
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
              transition: {
                duration: 0.25,
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.15,
              },
            }}
          >
            <FlexItems
              flexDirection="column"
              gap={4}
              className={styles.bottomSubMenuContent}
              alignItems="start"
            >
              {Items.map((Item, index) => (
                <FlexItem key={index}>{Item}</FlexItem>
              ))}
            </FlexItems>
          </motion.div>
        </CustomDivMotion>,
        !collapsed,
      )}
      {!collapsed &&
        bodyElement &&
        createPortal(
          <CustomButtonMotion
            className={styles.bottomSubMenuOverlay}
            data-testid="bottom-sub-menu-overlay"
            onClick={() => onOverlayClick?.()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
            }}
            transition={{ duration: 0.3 }}
          />,
          bodyElement,
        )}
    </>
  );
}
